/**
 * RequestsQueue — Admin Phase 1b · live concierge-request queue.
 *
 * Founder ruling 14 Jun 2026 PM (later · C8 · then C9.1 polish):
 *   "Strictly read-only. Server-side redacted projection. Role-shaped
 *    visibility. Surfaces rows from /escape /enjoy /table /club out of
 *    lc_concierge_requests. C9.1 corrects the API path (was double-prefixed
 *    in C8 · `api` already carries baseURL=${BACKEND_URL}/api) and brings
 *    the UI in line with the Control Room shell polish."
 *
 * Reads: GET /api/os-admin/requests
 *        GET /api/os-admin/requests/{ref}
 */
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

const SURFACE_LABELS = {
  escape: "Escape",
  enjoy: "Enjoy",
  table: "Table",
  club: "Club",
};

const STATUSES = [
  "new", "intake_complete", "desk_replied", "closed_won", "closed_lost",
];
const URGENCIES = ["low", "normal", "high", "urgent"];

function pillToneForStatus(s) {
  if (s === "new") return "os-p-new";
  if (s === "intake_complete") return "os-p-prog";
  if (s === "desk_replied") return "os-p-prog";
  if (s === "closed_won") return "os-p-done";
  if (s === "closed_lost") return "os-p-urgent";
  return "os-p-new";
}

function pillToneForUrgency(u) {
  if (u === "urgent" || u === "high") return "os-p-urgent";
  if (u === "normal") return "os-p-new";
  return "os-p-done";
}

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return iso;
  }
}

export default function RequestsQueue() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    total: 0, page: 1, per_page: 25,
    role: "read_only", has_more: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    surface: [],
    status: [],
    urgency: [],
    has_sensitivity: null,
    q: "",
    page: 1,
    per_page: 25,
  });

  const buildQS = (f) => {
    const p = new URLSearchParams();
    (f.surface || []).forEach((s) => p.append("surface", s));
    (f.status || []).forEach((s) => p.append("status", s));
    (f.urgency || []).forEach((u) => p.append("urgency", u));
    if (f.has_sensitivity === true) p.set("has_sensitivity", "true");
    if (f.has_sensitivity === false) p.set("has_sensitivity", "false");
    if ((f.q || "").trim()) p.set("q", f.q.trim());
    p.set("page", String(f.page));
    p.set("per_page", String(f.per_page));
    return p.toString();
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const qs = buildQS(filters);
        const r = await api.get(`/os-admin/requests?${qs}`);
        if (cancelled) return;
        setRows(r.data.rows || []);
        setMeta({
          total: r.data.total || 0,
          page: r.data.page || 1,
          per_page: r.data.per_page || 25,
          role: r.data.role || "read_only",
          has_more: !!r.data.has_more,
        });
      } catch (e) {
        if (cancelled) return;
        const friendly =
          formatApiError(e?.response?.data?.detail) ||
          "We could not load the requests queue just now. Please retry in a moment.";
        setError(friendly);
        setRows([]);
        setMeta((m) => ({ ...m, total: 0, has_more: false }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filters]);

  const toggleSet = (key, value) => {
    setFilters((prev) => {
      const cur = new Set(prev[key] || []);
      cur.has(value) ? cur.delete(value) : cur.add(value);
      return { ...prev, [key]: Array.from(cur), page: 1 };
    });
  };

  const isReadOnly = meta.role === "read_only";

  return (
    <div className="os-fyi-queue" data-testid="os-requests-queue">
      <div className="os-pagehead">
        <h1>Requests</h1>
        <span className="count">
          {loading ? "Loading…" : `${meta.total} rows`}
        </span>
      </div>
      <div className="os-pagesub">
        Live spine queue · <code>lc_concierge_requests</code> · role: <strong data-testid="os-queue-role">{meta.role}</strong> · server-side redaction applied
      </div>

      {/* ── Filter group ─────────────────────────────────────── */}
      <div className="os-fyi-filterblock" data-testid="os-queue-filters">
        <div className="os-toolbar">
          <span className="os-fyi-label">Surface</span>
          {Object.entries(SURFACE_LABELS).map(([k, label]) => (
            <button
              key={k}
              type="button"
              className={`os-fchip ${filters.surface.includes(k) ? "on" : ""}`}
              onClick={() => toggleSet("surface", k)}
              data-testid={`os-filter-surface-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="os-toolbar">
          <span className="os-fyi-label">Status</span>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`os-fchip ${filters.status.includes(s) ? "on" : ""}`}
              onClick={() => toggleSet("status", s)}
              data-testid={`os-filter-status-${s}`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>

        <div className="os-toolbar">
          <span className="os-fyi-label">Urgency</span>
          {URGENCIES.map((u) => (
            <button
              key={u}
              type="button"
              className={`os-fchip ${filters.urgency.includes(u) ? "on" : ""}`}
              onClick={() => toggleSet("urgency", u)}
              data-testid={`os-filter-urgency-${u}`}
            >
              {u}
            </button>
          ))}

          <span className="os-fyi-label os-fyi-label-spaced">Sensitivity</span>
          {[
            { key: "any", value: null, label: "any" },
            { key: "yes", value: true, label: "present" },
            { key: "no", value: false, label: "none" },
          ].map((o) => (
            <button
              key={o.key}
              type="button"
              className={`os-fchip ${filters.has_sensitivity === o.value ? "on" : ""}`}
              onClick={() => setFilters((p) => ({ ...p, has_sensitivity: o.value, page: 1 }))}
              data-testid={`os-filter-sensitivity-${o.key}`}
            >
              {o.label}
            </button>
          ))}

          <span className="os-spacer" />
          <input
            type="search"
            placeholder="Search ref · venue · city · rail"
            value={filters.q}
            onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value, page: 1 }))}
            maxLength={80}
            className="os-fyi-search"
            data-testid="os-queue-search-input"
          />
        </div>
      </div>

      {/* ── Table or message ─────────────────────────────────── */}
      {error ? (
        <div className="os-fyi-msg os-fyi-msg-err" data-testid="os-queue-error">
          {error}
        </div>
      ) : rows.length === 0 && !loading ? (
        <div className="os-fyi-msg" data-testid="os-queue-empty">
          No requests match these filters.
        </div>
      ) : (
        <table className="os-tbl" data-testid="os-queue-table">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Surface</th>
              <th>Title</th>
              <th>Member</th>
              <th>Status</th>
              <th>Urgency</th>
              <th>Sensitivity</th>
              {!isReadOnly && <th>Zoho</th>}
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.ref} data-testid={`os-queue-row-${r.ref}`}>
                <td>
                  <NavLink
                    to={`/admin/operate/requests/${r.ref}`}
                    className="os-ref"
                    data-testid={`os-queue-row-link-${r.ref}`}
                  >
                    {r.ref}
                  </NavLink>
                </td>
                <td>
                  <span className="os-surf">{SURFACE_LABELS[r.surface] || r.surface}</span>
                </td>
                <td>
                  <div className="os-fyi-title">
                    {(r.entity && (r.entity.title || r.entity.venue)) ||
                      (r.request && r.request.summary_short) || "—"}
                  </div>
                  {r.entity?.city && <div className="os-fyi-sub">{r.entity.city}</div>}
                </td>
                <td>
                  {r.member?.first || "—"}
                  {r.member?.last ? ` ${r.member.last}` : ""}
                </td>
                <td>
                  <span className={`os-pill ${pillToneForStatus(r.status)}`}>
                    {(r.status || "").replace("_", " ") || "—"}
                  </span>
                </td>
                <td>
                  <span className={`os-pill ${pillToneForUrgency(r.urgency)}`}>
                    {r.urgency || "—"}
                  </span>
                </td>
                <td>
                  {Array.isArray(r.sensitivity_flags) ? (
                    r.sensitivity_flags.length > 0 ? (
                      <span className="os-fyi-flagrow">
                        {r.sensitivity_flags.map((f) => (
                          <span
                            key={f}
                            className="os-pill os-p-urgent"
                            data-testid={`os-flag-${f}`}
                          >
                            {f}
                          </span>
                        ))}
                      </span>
                    ) : <span className="os-fyi-dim">—</span>
                  ) : r.sensitivity_present ? (
                    <span
                      className="os-pill os-p-prog"
                      title="At least one sensitivity flag"
                      data-testid="os-flag-present"
                    >present</span>
                  ) : <span className="os-fyi-dim">—</span>}
                </td>
                {!isReadOnly && (
                  <td>
                    {r.zoho?.ticket_id ? (
                      r.zoho.deeplink ? (
                        <a
                          href={r.zoho.deeplink}
                          target="_blank"
                          rel="noreferrer"
                          className="os-ref"
                          data-testid={`os-row-zoho-${r.ref}`}
                        >
                          {r.zoho.ticket_number || r.zoho.ticket_id}
                        </a>
                      ) : (
                        <span className="os-ref" data-testid={`os-row-zoho-${r.ref}`}>
                          {r.zoho.ticket_id}
                        </span>
                      )
                    ) : <span className="os-fyi-dim">—</span>}
                  </td>
                )}
                <td title={r.created_at}>{relativeTime(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── Pagination ───────────────────────────────────────── */}
      <div className="os-fyi-pagi" data-testid="os-queue-pagination">
        <button
          type="button"
          className="os-btn-ghost"
          disabled={meta.page <= 1 || loading}
          onClick={() => setFilters((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
          data-testid="os-queue-page-prev"
        >‹ Prev</button>
        <span className="os-fyi-pagemark">
          Page {meta.page} · {meta.per_page}/page
        </span>
        <button
          type="button"
          className="os-btn-ghost"
          disabled={!meta.has_more || loading}
          onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
          data-testid="os-queue-page-next"
        >Next ›</button>
      </div>
    </div>
  );
}
